import { allInTotalFromFare, estimatedDepositFromFare } from '@/utils';
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, Plane, MapPin, Calendar, Clock, TrendingDown, Info } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  BRAND_NESTED_ROW,
  BRAND_PRIMARY_BTN_CLASS,
  REWARDS_SECTION_CARD,
} from '@/constants/brandColors';
import { cn } from '@/lib/utils';


export default function DestinationPreviewOverlay({ destination, origin, onClose, onViewFlights , showAllInPrices = true, deferaCapitalTier = false }) {
  if (!destination) return null;

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    }
    return `${mins}m`;
  };

  const formatStops = (stops) => {
    if (stops === 0) return 'Nonstop';
    if (stops === 1) return '1 stop';
    return `${stops} stops`;
  };

  const flightPrice = destination.flight_price && !isNaN(parseFloat(destination.flight_price))
    ? parseFloat(destination.flight_price)
    : null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-brand-950/85 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 24, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            REWARDS_SECTION_CARD,
            'relative w-full sm:max-w-lg max-h-[92vh] overflow-hidden flex flex-col',
            'rounded-t-2xl sm:rounded-2xl',
          )}
        >
          {/* Hero */}
          <div className="relative shrink-0">
            {destination.thumbnail ? (
              <div className="relative h-44 sm:h-48 w-full overflow-hidden">
                <img
                  src={destination.thumbnail}
                  alt=""
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-950 via-brand-950/40 to-transparent" />
              </div>
            ) : (
              <div className="h-24 bg-gradient-to-b from-brand-800/30 to-transparent" />
            )}

            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-brand-950/70 border border-white/10 flex items-center justify-center text-white/80 hover:text-white hover:bg-brand-950/90 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className={cn('px-5 pb-1', destination.thumbnail ? '-mt-12 relative z-10' : 'pt-5')}>
              <h2 className="text-2xl font-light text-white leading-tight">{destination.name}</h2>
              <p className="text-sm text-white/60 mt-0.5">{destination.country}</p>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 pb-5 pt-4 space-y-5">
            <section>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-white/90">Travel from {origin}</h3>
                <Info className="w-4 h-4 text-white/35 shrink-0" aria-hidden />
              </div>

              <div className="bg-brand-500/10 border border-brand-500/25 rounded-xl px-3 py-2.5 mb-3">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-4 h-4 text-brand-400 shrink-0" />
                  <p className="text-sm text-brand-200/90 font-normal">
                    Prices are currently <span className="font-medium text-brand-100">low</span> — great time to book
                  </p>
                </div>
              </div>

              <div className={cn(BRAND_NESTED_ROW, 'p-4')}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-brand-900/50 border border-brand-800/35 flex items-center justify-center shrink-0">
                      <Plane className="w-4 h-4 text-white/55" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-white">{destination.airline || 'Multiple airlines'}</p>
                      <p className="text-xs text-white/50 mt-0.5">
                        {formatStops(destination.number_of_stops || 0)}
                        {destination.flight_duration && (
                          <>
                            {' · '}
                            <span className="inline-flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDuration(destination.flight_duration)}
                            </span>
                          </>
                        )}
                      </p>
                      <p className="text-xs text-white/45 mt-2 inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3 shrink-0" />
                        {origin} → {destination.destination_airport?.code || destination.name}
                      </p>
                    </div>
                  </div>

                  <div className="text-right shrink-0">
                    {flightPrice != null ? (
                      <>
                        <p className="text-xl font-medium text-white tabular-nums">
                          ${Math.round(estimatedDepositFromFare(flightPrice, destination.start_date && Number.isFinite(Date.parse(destination.start_date)) ? Math.floor((Date.parse(destination.start_date) - Date.now()) / 86400000) : null, deferaCapitalTier)).toLocaleString('en-US')} today
                        </p>
                        <p key={showAllInPrices ? 'allin' : 'fare'} className="text-xs text-white/50 tabular-nums transform-gpu">
                          {showAllInPrices
                            ? `$${Math.ceil(allInTotalFromFare(flightPrice, deferaCapitalTier)).toLocaleString('en-US')} total`
                            : `$${destination.flight_price} fare`}
                        </p>
                        <p className="text-[11px] text-white/40 mt-0.5">Round trip</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xl font-medium text-white">N/A</p>
                        <p className="text-[11px] text-white/40 mt-0.5">Round trip</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </section>

            {destination.start_date && (
              <section className="pt-4 border-t border-brand-800/25">
                <div className="flex items-center gap-2 text-white/45 mb-1.5">
                  <Calendar className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wide">Travel dates</span>
                </div>
                <p className="text-sm text-white font-normal">
                  {format(parseISO(destination.start_date), 'EEE, MMM d')}
                  {destination.end_date && ` – ${format(parseISO(destination.end_date), 'EEE, MMM d')}`}
                </p>
              </section>
            )}

            {destination.hotel_price && (
              <section className="pt-4 border-t border-brand-800/25">
                <p className="text-xs text-white/45 uppercase tracking-wide mb-1.5">Hotel</p>
                <p className="text-sm text-white font-normal">${destination.hotel_price} per night</p>
              </section>
            )}

            <Button
              onClick={onViewFlights}
              className={cn('w-full h-11 rounded-full mt-1', BRAND_PRIMARY_BTN_CLASS)}
            >
              View flights
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
